import type { ScoutChatMessagePart, ScoutToolPart } from "./message/scoutChatMessage";

/** Minimal message shape for conversation-level todo epoch derivation. */
export type ScoutTodoConversationMessage = {
  role: string;
  parts: ScoutChatMessagePart[];
  isStreaming?: boolean;
};

export type ScoutTodoStatus = "pending" | "in_progress" | "interrupted" | "done" | "skipped";

export type ScoutTodoItem = {
  id: string;
  task: string;
  /** Customer-facing step name when provided by the agent. */
  label?: string;
  status: ScoutTodoStatus;
  result?: string;
  source?: string;
};

export type ScoutTodoSummary = {
  total: number;
  pending: number;
  in_progress: number;
  interrupted: number;
  done: number;
  skipped: number;
};

export type ScoutTodoSnapshot = {
  items: ScoutTodoItem[];
  summary: ScoutTodoSummary;
  /** True once create_todos has been invoked in this tool group. */
  hasChecklist: boolean;
  /** True while create_todos is still running without a parsed item list. */
  isPlanning: boolean;
};

export type DeriveTodoSnapshotOptions = {
  /** When false, in_progress items are shown as interrupted (turn ended mid-step). */
  isStreaming?: boolean;
};

export const SCOUT_TODO_TOOL_NAMES = new Set([
  "create_todos",
  "update_todo",
  "update_todos",
  "get_todos",
]);

const TODO_STATUSES = new Set<ScoutTodoStatus>([
  "pending",
  "in_progress",
  "interrupted",
  "done",
  "skipped",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function parseJsonObjectIfPossible(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Accept tool args as a decoded object or a JSON string (streamed / persisted). */
function normalizeToolInput(input: unknown): Record<string, unknown> | null {
  if (isRecord(input)) {
    return input;
  }
  if (typeof input === "string") {
    const parsed = parseJsonObjectIfPossible(input);
    return isRecord(parsed) ? parsed : null;
  }
  return null;
}

function parseTodoStatus(value: unknown): ScoutTodoStatus | null {
  if (typeof value === "string" && TODO_STATUSES.has(value as ScoutTodoStatus)) {
    return value as ScoutTodoStatus;
  }
  return null;
}

/**
 * Parse a field that may be either a JSON-encoded string (how the agent SDK
 * sends `create_todos.items` / `update_todos.updates`) or an already-decoded
 * array (how tests / direct calls pass it).
 */
function parseJsonArrayField(value: unknown, key: string): Record<string, unknown>[] {
  const record = normalizeToolInput(value);
  if (!record || !(key in record)) return [];
  let candidate = record[key];
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(candidate)) return [];
  return candidate.filter(isRecord);
}

function parseTodoItem(record: Record<string, unknown>): ScoutTodoItem | null {
  const id = readString(record, "id");
  if (!id) return null;

  const task = readString(record, "task") ?? id;
  const label = readString(record, "label");
  const status = parseTodoStatus(record.status) ?? "pending";
  const result = readString(record, "result");
  const source = readString(record, "source");

  return {
    id,
    task,
    status,
    ...(label ? { label } : {}),
    ...(result ? { result } : {}),
    ...(source ? { source } : {}),
  };
}

function parseTodoItemsFromInput(input: unknown, key: string): ScoutTodoItem[] {
  return parseJsonArrayField(input, key)
    .map(parseTodoItem)
    .filter((item): item is ScoutTodoItem => item !== null);
}

function parseTodoItemFromOutput(output: unknown): ScoutTodoItem | null {
  if (!isRecord(output)) return null;
  const itemRecord = isRecord(output.item) ? output.item : output;
  return parseTodoItem(itemRecord);
}

function parseTodoItemsFromOutput(output: unknown): ScoutTodoItem[] {
  if (!isRecord(output)) return [];
  const itemsValue = output.items;
  if (!Array.isArray(itemsValue)) return [];
  return itemsValue
    .filter(isRecord)
    .map(parseTodoItem)
    .filter((item): item is ScoutTodoItem => item !== null);
}

export function calculateTodoSummary(items: ScoutTodoItem[]): ScoutTodoSummary {
  return {
    total: items.length,
    pending: items.filter((item) => item.status === "pending").length,
    in_progress: items.filter((item) => item.status === "in_progress").length,
    interrupted: items.filter((item) => item.status === "interrupted").length,
    done: items.filter((item) => item.status === "done").length,
    skipped: items.filter((item) => item.status === "skipped").length,
  };
}

function normalizeItemsForDisplay(items: ScoutTodoItem[], isStreaming: boolean): ScoutTodoItem[] {
  if (isStreaming) {
    return items;
  }

  return items.map((item) => {
    if (item.status !== "in_progress") {
      return item;
    }
    return { ...item, status: "interrupted" };
  });
}

function replaceAllItems(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  nextItems: ScoutTodoItem[]
): void {
  itemsById.clear();
  order.length = 0;
  for (const item of nextItems) {
    itemsById.set(item.id, item);
    order.push(item.id);
  }
}

function itemsHaveOpenWork(items: ReadonlyArray<ScoutTodoItem>): boolean {
  return items.some(
    (item) =>
      item.status === "pending" || item.status === "in_progress" || item.status === "interrupted"
  );
}

/**
 * Scout sometimes calls create_todos again on resume with fresh pending items.
 * Keep done/skipped progress from the interrupted checklist by id.
 */
function mergeCreatedItemsPreservingProgress(
  previous: ReadonlyArray<ScoutTodoItem>,
  next: ReadonlyArray<ScoutTodoItem>
): ScoutTodoItem[] {
  const previousById = new Map(previous.map((item) => [item.id, item]));
  return next.map((item) => {
    const prior = previousById.get(item.id);
    if (!prior) {
      return item;
    }
    if (prior.status !== "done" && prior.status !== "skipped") {
      return item;
    }
    if (item.status === "done" || item.status === "skipped") {
      return {
        ...prior,
        ...item,
        status: item.status,
      };
    }
    return prior;
  });
}

function applyTodoUpdate(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  update: Partial<ScoutTodoItem> & { id: string }
): void {
  const existing = itemsById.get(update.id);
  const nextStatus = update.status ?? existing?.status ?? "pending";
  // Don't let a resume recreate regress completed items back to open work.
  if (
    existing &&
    (existing.status === "done" || existing.status === "skipped") &&
    nextStatus !== "done" &&
    nextStatus !== "skipped"
  ) {
    return;
  }

  const merged: ScoutTodoItem = {
    id: update.id,
    task: update.task ?? existing?.task ?? update.id,
    status: nextStatus,
    ...(update.result !== undefined
      ? { result: update.result }
      : existing?.result
        ? { result: existing.result }
        : {}),
    ...(update.label !== undefined
      ? { label: update.label }
      : existing?.label
        ? { label: existing.label }
        : {}),
    ...(update.source !== undefined
      ? { source: update.source }
      : existing?.source
        ? { source: existing.source }
        : {}),
  };

  if (!itemsById.has(update.id)) {
    order.push(update.id);
  }
  itemsById.set(update.id, merged);
}

function applyUpdateTodoFromInput(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  toolInput: unknown
): void {
  const inputRecord = normalizeToolInput(toolInput);
  if (!inputRecord) {
    return;
  }

  const id = readString(inputRecord, "id");
  const status = parseTodoStatus(inputRecord.status);
  if (!id || !status) {
    return;
  }

  applyTodoUpdate(itemsById, order, {
    id,
    status,
    ...(readString(inputRecord, "result") ? { result: readString(inputRecord, "result") } : {}),
    ...(readString(inputRecord, "source") ? { source: readString(inputRecord, "source") } : {}),
  });
}

function applyUpdateTodosFromInput(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  toolInput: unknown
): void {
  const inputUpdates = parseTodoItemsFromInput(toolInput, "updates");
  for (const update of inputUpdates) {
    applyTodoUpdate(itemsById, order, update);
  }
}

function isToolRunning(tool: ScoutToolPart): boolean {
  return tool.toolState === "input_streaming" || tool.toolState === "input_available";
}

/**
 * Reconstruct the current todo checklist from all tool parts on an assistant
 * message. Aggregates todo events across tool groups separated by text blocks.
 */
export function deriveTodoSnapshotFromMessageParts(
  parts: ScoutChatMessagePart[],
  options?: DeriveTodoSnapshotOptions
): ScoutTodoSnapshot | null {
  const toolParts = parts.filter((part): part is ScoutToolPart => part.type === "tool");
  return deriveTodoSnapshotFromToolParts(toolParts, options);
}

/**
 * Reconstruct the current todo checklist from todo tool parts in stream order.
 * Returns null when the tool group contains no todo tools.
 */
export function deriveTodoSnapshotFromToolParts(
  toolParts: ScoutToolPart[],
  options?: DeriveTodoSnapshotOptions
): ScoutTodoSnapshot | null {
  const todoTools = toolParts.filter((tool) => SCOUT_TODO_TOOL_NAMES.has(tool.toolName));
  if (todoTools.length === 0) {
    return null;
  }

  const itemsById = new Map<string, ScoutTodoItem>();
  const order: string[] = [];
  let hasChecklist = false;
  let isPlanning = false;

  for (const tool of todoTools) {
    switch (tool.toolName) {
      case "create_todos": {
        hasChecklist = true;
        const items = parseTodoItemsFromInput(tool.toolInput, "items");
        if (items.length > 0) {
          const existing = order
            .map((id) => itemsById.get(id))
            .filter((item): item is ScoutTodoItem => item !== undefined);
          if (existing.length > 0 && itemsHaveOpenWork(existing)) {
            replaceAllItems(itemsById, order, mergeCreatedItemsPreservingProgress(existing, items));
          } else {
            replaceAllItems(itemsById, order, items);
          }
          isPlanning = false;
        } else if (isToolRunning(tool)) {
          isPlanning = true;
        }
        break;
      }
      case "update_todo": {
        hasChecklist = true;
        const outputItem = parseTodoItemFromOutput(tool.toolOutput);
        if (outputItem) {
          applyTodoUpdate(itemsById, order, outputItem);
          break;
        }

        applyUpdateTodoFromInput(itemsById, order, tool.toolInput);
        break;
      }
      case "update_todos": {
        hasChecklist = true;
        const outputItems = parseTodoItemsFromOutput(tool.toolOutput);
        if (outputItems.length > 0) {
          for (const item of outputItems) {
            applyTodoUpdate(itemsById, order, item);
          }
          break;
        }

        applyUpdateTodosFromInput(itemsById, order, tool.toolInput);
        break;
      }
      // get_todos is ignored for UI state — verification only.
      case "get_todos":
        break;
      default:
        break;
    }
  }

  const rawItems = order
    .map((id) => itemsById.get(id))
    .filter((item): item is ScoutTodoItem => item !== undefined);

  if (isPlanning && rawItems.length > 0) {
    isPlanning = false;
  }

  const isStreaming = options?.isStreaming ?? false;
  const items = normalizeItemsForDisplay(rawItems, isStreaming);

  return {
    items,
    summary: calculateTodoSummary(items),
    hasChecklist,
    isPlanning,
  };
}

/**
 * True while the conversation checklist still has open work (including an
 * in-flight create_todos). Done/skipped-only checklists are considered
 * finished for UI anchoring.
 */
export function isScoutTodoChecklistIncomplete(
  snapshot: ScoutTodoSnapshot | null | undefined
): boolean {
  if (!snapshot?.hasChecklist) {
    return false;
  }
  if (snapshot.isPlanning) {
    return true;
  }
  const { summary } = snapshot;
  return summary.pending > 0 || summary.in_progress > 0 || summary.interrupted > 0;
}

/**
 * ## Epoch & checklist hosting (conversation state machine)
 *
 * An **epoch** is one checklist lifecycle: tools from the opening
 * `create_todos` through updates (and optional resume recreates) until a later
 * `create_todos` opens a *new* list after this one is finished, or the thread
 * ends. Each epoch yields one `ScoutTodoEpoch` with:
 * - `createMessageId` — assistant turn that first called `create_todos`
 * - `hostMessageId` — turn that renders the checklist (create turn, or the
 *   latest resume turn that contributed todo tools)
 * - `anchorToolCallId` — tool call to place the list *after* in that host
 * - `snapshot` — aggregated items from every todo tool in the epoch
 *
 * `AgentMessage` only paints epochs where `hostMessageId` matches; finished
 * lists stay put, incomplete ones relocate onto the resume turn.
 *
 * ### Incompleteness
 * Incomplete if `isPlanning` or any item is pending / in_progress /
 * interrupted. Done+skipped-only (and not planning) = finished.
 *
 * ### Per todo tool (assistant stream order)
 * ```
 * get_todos          → ignore (never opens / updates / relocates)
 * create_todos       → if open epoch is incomplete: append (same epoch, resume)
 *                      else: close open (if any), open new epoch
 * update_todo(s)     → if open epoch: append; else ignore orphan
 * ```
 * After the last message, close any open epoch.
 *
 * ### Host / anchor on finalize
 * - One contributing message → host = create, anchor = create tool call
 * - Later turn(s) contributed todo tools → host = last contributor, anchor =
 *   first todo tool call on that host message
 *
 * See `deriveConversationTodoEpochs` / `getHostedTodoEpochsForMessage`.
 */
export type ScoutTodoEpoch = {
  snapshot: ScoutTodoSnapshot;
  /** Assistant message that called create_todos for this checklist. */
  createMessageId: string;
  /**
   * Message that should render this checklist.
   * Equals createMessageId unless a later turn resumed the same incomplete list.
   */
  hostMessageId: string;
  /**
   * Tool call to place the checklist after in the host message (create_todos
   * on the create turn; first todo tool on a resume turn).
   */
  anchorToolCallId: string;
};

type ScoutTodoEpochMessage = ScoutTodoConversationMessage & { id: string };

type OpenTodoEpoch = {
  createMessageId: string;
  createToolCallId: string;
  tools: ScoutToolPart[];
  contributingMessageIds: string[];
  hostAnchorByMessageId: Map<string, string>;
};

function appendTodoToolToOpenEpoch(
  epoch: OpenTodoEpoch,
  messageId: string,
  tool: ScoutToolPart
): void {
  epoch.tools.push(tool);
  if (!epoch.contributingMessageIds.includes(messageId)) {
    epoch.contributingMessageIds.push(messageId);
  }
  if (!epoch.hostAnchorByMessageId.has(messageId)) {
    epoch.hostAnchorByMessageId.set(messageId, tool.toolCallId);
  }
}

function finalizeTodoEpoch(
  open: OpenTodoEpoch,
  messagesById: Map<string, ScoutTodoEpochMessage>
): ScoutTodoEpoch | null {
  const lastContributingId =
    open.contributingMessageIds[open.contributingMessageIds.length - 1] ?? open.createMessageId;
  // Only relocate when a later turn contributed todo tools (true resume).
  const hostMessageId =
    open.contributingMessageIds.length > 1 ? lastContributingId : open.createMessageId;
  const hostMessage = messagesById.get(hostMessageId);
  const snapshot = deriveTodoSnapshotFromToolParts(open.tools, {
    isStreaming: hostMessage?.isStreaming ?? false,
  });
  if (!snapshot?.hasChecklist) {
    return null;
  }

  const anchorToolCallId =
    hostMessageId === open.createMessageId
      ? open.createToolCallId
      : (open.hostAnchorByMessageId.get(hostMessageId) ?? open.createToolCallId);

  return {
    snapshot,
    createMessageId: open.createMessageId,
    hostMessageId,
    anchorToolCallId,
  };
}

/**
 * Walk assistant messages and split todo tools into epochs. Decision tree and
 * host/anchor rules: see `ScoutTodoEpoch`.
 */
export function deriveConversationTodoEpochs(
  messages: ReadonlyArray<ScoutTodoEpochMessage>
): ScoutTodoEpoch[] {
  const messagesById = new Map(messages.map((message) => [message.id, message]));
  const epochs: ScoutTodoEpoch[] = [];
  let open: OpenTodoEpoch | null = null;

  const closeOpen = () => {
    if (!open) {
      return;
    }
    const finalized = finalizeTodoEpoch(open, messagesById);
    if (finalized) {
      epochs.push(finalized);
    }
    open = null;
  };

  for (const message of messages) {
    if (message.role !== "assistant") {
      continue;
    }

    for (const part of message.parts) {
      if (part.type !== "tool" || !SCOUT_TODO_TOOL_NAMES.has(part.toolName)) {
        continue;
      }

      if (part.toolName === "get_todos") {
        // Verification only — never opens, updates, or relocates a checklist.
        continue;
      }

      if (part.toolName === "create_todos") {
        if (open) {
          const current = deriveTodoSnapshotFromToolParts(open.tools, {
            isStreaming: false,
          });
          if (current && isScoutTodoChecklistIncomplete(current)) {
            // Resume recreated the list — keep one checklist and relocate it.
            appendTodoToolToOpenEpoch(open, message.id, part);
            continue;
          }
        }
        closeOpen();
        open = {
          createMessageId: message.id,
          createToolCallId: part.toolCallId,
          tools: [part],
          contributingMessageIds: [message.id],
          hostAnchorByMessageId: new Map([[message.id, part.toolCallId]]),
        };
        continue;
      }

      // update_* without a create_todos yet: attach to the open epoch (resume)
      // or ignore orphans from a missing create.
      if (!open) {
        continue;
      }
      appendTodoToolToOpenEpoch(open, message.id, part);
    }
  }

  closeOpen();
  return epochs;
}

/** Hosted checklist views for a single assistant message. */
export function getHostedTodoEpochsForMessage(
  epochs: ReadonlyArray<ScoutTodoEpoch>,
  messageId: string
): ScoutTodoEpoch[] {
  return epochs.filter((epoch) => epoch.hostMessageId === messageId);
}
